import { ArrowForwardIos, FiberManualRecord } from '@mui/icons-material';
import { IconButton, Typography, useTheme } from '@mui/material';
import { useFeature } from '@next-box/feature-flags';
import { ScreenType } from '@tmdb-graphql-api/schema/types';
import { startCase } from 'lodash-es';
import { Component as CertificationIcon } from '#components/CertificationIcon/index.ts';
import { ConditionalTextLink } from '#components/ConditionalTextLink/index.tsx';
import { type MovieDetailsProps } from '#components/Details/Movie/index.tsx';
import {
  CertificationIconContainer,
  Container,
  GenresContainer,
  RatingsContainer,
  SectionA,
} from '#components/Details/styled.ts';
import { type CommonDetailsHeaderProps } from '#components/Details/types.ts';
import { FavoriteBadge } from '#components/FavoriteBadge/index.tsx';
import { Link } from '#components/Link/index.tsx';
import { Optional } from '#components/Optional/index.tsx';
import { PopularityBadge } from '#components/PopularityBadge/index.tsx';
import { Query as ReviewsBadge } from '#components/ReviewsBadge/index.ts';
import { Large as VoteAverageBadge } from '#components/VoteAverageBadge/index.tsx';
import { WatchlistBadge } from '#components/WatchlistBadge/index.tsx';
import { FeatureFlags } from '#enums/featureFlags.ts';
import { formatGenres } from '#helpers/formatGenres.ts';
import { formatReleaseDate } from '#helpers/formatReleaseDate.ts';
import { formatRuntime } from '#helpers/formatRuntime.ts';
import { getMovieCertFromReleaseDates } from '#helpers/getMovieCertFromReleaseDates.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';

export type MovieDetailsHeaderProps = MovieDetailsProps & CommonDetailsHeaderProps;

export const MovieDetailsHeader = ({
  compact = false,
  data,
  linkTitle = false,
  showExpandIcon = false,
  sx,
  titleLinkOnClick,
}: MovieDetailsHeaderProps) => {
  const { genres, id, popularity, releaseDate, releaseDates, runtime, status, tagline, title, voteAverage, voteCount } =
    data;

  const theme = useTheme();
  const { config } = useToolbelt();
  const watchlistEnabled = useFeature(FeatureFlags.Watchlist);
  const sectionTypeKey = 'movie';
  const certification = getMovieCertFromReleaseDates(releaseDates ?? [], config.country);
  const formattedGenres = formatGenres(genres);
  const formattedRuntime = formatRuntime(runtime);

  return (
    <Container sx={sx}>
      <Typography
        sx={{
          fontSize: compact ? '1.8rem' : undefined,
          paddingRight: showExpandIcon ? '52px' : undefined,
          position: 'relative',
        }}
        variant="h4"
      >
        <ConditionalTextLink
          link={linkTitle}
          onClick={titleLinkOnClick}
          to={`/details/${sectionTypeKey}/${String(id)}`}
        >
          <strong>{title}</strong>
        </ConditionalTextLink>
        {showExpandIcon ? (
          <IconButton
            aria-label="Open preview in full"
            component={Link}
            sx={{
              color: theme.palette.grey[100],
              position: 'absolute',
              right: 0,
              top: 0,
            }}
            to={`/details/${sectionTypeKey}/${String(id)}`}
          >
            <ArrowForwardIos />
          </IconButton>
        ) : undefined}
      </Typography>
      <Optional>
        <Typography
          sx={{
            fontSize: compact ? '1rem' : undefined,
          }}
          variant="h6"
        >
          {tagline}
        </Typography>
      </Optional>
      <div>
        <Typography sx={{ color: theme.supportingTextPrimary }} variant="body2">
          <span>{releaseDate ? formatReleaseDate(releaseDate) : startCase(status.toLowerCase())}</span>
          {!compact && formattedGenres ? (
            <GenresContainer>
              <FiberManualRecord
                sx={{ height: '10px', marginLeft: theme.spacing(1), marginRight: theme.spacing(1), width: '10px' }}
              />
              <span>{formattedGenres}</span>
            </GenresContainer>
          ) : undefined}
          {formattedRuntime ? (
            <>
              <FiberManualRecord
                sx={{ height: '10px', marginLeft: theme.spacing(1), marginRight: theme.spacing(1), width: '10px' }}
              />
              <span>{formattedRuntime}</span>
            </>
          ) : undefined}
        </Typography>
      </div>
      <SectionA sx={{ display: 'flex', marginBottom: theme.spacing(1.5), marginTop: theme.spacing(1.5) }}>
        {certification ? (
          <CertificationIconContainer>
            <CertificationIcon
              certification={certification}
              height={compact ? 35 : 50}
              id={id}
              sx={{
                [theme.breakpoints.down('sm')]: {
                  height: 35,
                  width: 35,
                },
              }}
              type={ScreenType.Movie}
              width={compact ? 35 : 50}
            />
          </CertificationIconContainer>
        ) : undefined}
        <RatingsContainer>
          <ReviewsBadge compact={compact} id={id} title={title} type={ScreenType.Movie} />
          {watchlistEnabled ? (
            <WatchlistBadge
              compact={compact}
              id={id}
              status={status}
              sx={{ marginLeft: theme.spacing(2) }}
              type={ScreenType.Movie}
            />
          ) : undefined}
          <FavoriteBadge compact={compact} id={id} sx={{ marginLeft: theme.spacing(2) }} type={ScreenType.Movie} />
          <VoteAverageBadge
            compact={compact}
            sx={{
              [theme.breakpoints.up('md')]: {
                marginLeft: compact ? theme.spacing(1) : theme.spacing(2),
              },
              marginLeft: compact ? theme.spacing(0.5) : theme.spacing(1),
            }}
            voteAverage={voteAverage}
            voteCount={voteCount}
          />
          {compact ? undefined : (
            <PopularityBadge
              popularity={popularity}
              sx={{
                [theme.breakpoints.up('md')]: {
                  marginLeft: theme.spacing(2),
                },
                [theme.breakpoints.down('sm')]: {
                  display: 'none',
                },
                marginLeft: theme.spacing(1),
              }}
            />
          )}
        </RatingsContainer>
      </SectionA>
    </Container>
  );
};
